import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {useCallback, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import CancelButton from "../components/dialog/CancelButton.tsx";
import SubmitButton from "../components/dialog/SubmitButton.tsx";
import DeleteButton from "../components/dialog/DeleteButton.tsx";

interface ConfirmOptions {
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    // When true the confirm action is destructive: it is de-emphasized and the
    // cancel ("safe") action becomes the primary CTA, so a reflexive click does
    // not trigger the irreversible action (#55076).
    danger?: boolean;
    // When true the confirm action itself reads as the dangerous one: it is
    // rendered as a red danger button while "cancel" stays a neutral outlined
    // button. Use this (instead of `danger`) for irreversible deletes /
    // cancellations where the confirm should be unmistakably destructive.
    destructive?: boolean;
}

interface DialogState extends ConfirmOptions {
    open: boolean;
}

const initialState: DialogState = {open: false, message: ""};

/**
 * Promise-based confirm dialog using the brand `Dialog` primitive.
 * Drop-in replacement for `window.confirm()`.
 *
 * Usage:
 *   const {confirm, dialog} = useConfirmDialog();
 *   const ok = await confirm({message: "Delete?"});
 *   ...
 *   return <>{children}{dialog}</>;
 */
export const useConfirmDialog = () => {
    const {t} = useTranslation();
    const [state, setState] = useState<DialogState>(initialState);
    const resolverRef = useRef<((accepted: boolean) => void) | null>(null);

    const confirm = useCallback(async (options: ConfirmOptions) => {
        return await new Promise<boolean>(resolve => {
            resolverRef.current = resolve;
            setState({open: true, ...options});
        });
    }, []);

    const close = useCallback((accepted: boolean) => {
        setState(prev => ({...prev, open: false}));
        resolverRef.current?.(accepted);
        resolverRef.current = null;
    }, []);

    const dialog = (
        <Dialog
            open={state.open}
            onClose={() => { close(false); }}
            maxWidth="xs"
            fullWidth
        >
            {state.title && <DialogTitle>{state.title}</DialogTitle>}
            <DialogContent>
                <DialogContentText sx={{color: "var(--an-ink)", whiteSpace: "pre-line"}}>
                    {state.message}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {state.destructive
                    ? <>
                        <CancelButton onClick={() => { close(false); }}>
                            {state.cancelLabel ?? t("defaults.cancel")}
                        </CancelButton>
                        <DeleteButton onClick={() => { close(true); }}>
                            {state.confirmLabel ?? t("defaults.confirm")}
                        </DeleteButton>
                    </>
                    : state.danger
                    ? <>
                        <CancelButton onClick={() => { close(true); }}>
                            {state.confirmLabel ?? t("defaults.confirm")}
                        </CancelButton>
                        <SubmitButton onClick={() => { close(false); }}>
                            {state.cancelLabel ?? t("defaults.cancel")}
                        </SubmitButton>
                    </>
                    : <>
                        <CancelButton onClick={() => { close(false); }}>
                            {state.cancelLabel ?? t("defaults.cancel")}
                        </CancelButton>
                        <SubmitButton onClick={() => { close(true); }}>
                            {state.confirmLabel ?? t("defaults.confirm")}
                        </SubmitButton>
                    </>
                }
            </DialogActions>
        </Dialog>
    );

    return {confirm, dialog};
};

export default useConfirmDialog;
